import { LocalDate } from "@js-joda/core";
import { isNil } from "rambda";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { Source } from "trader.ts/types/source";
import { dynVariadic } from "trader.ts/ui";
import { Form, selectBox, textBox } from "trader.ts/ui.forms";
import { mapPartial } from "trader.ts/utils";
import * as FA from "../../../styles/fontawesome.css.classes";
import * as style from "../../../styles/style.css.classes";
import { fileboxWithAutosave, fileInputField } from "../../client/fileinput";
import { renderSaveButton } from "../../client/savebutton";
import { arrayBufferToBase64Client } from "../../lib/arraybuffertobase64";
import { mandatoryValidation } from "../../lib/validate";
import { ddMMMMyyyy, wrapInParentheses } from "../../render";
import * as routes from "../../routes";
import { renderEnrollmentCard } from "../student_start/client";
import { enrollmentDetailsStudentStart } from "../student_start_lessons_notes/client";

export function render(opts: {
  enrollment: enrollmentDetailsStudentStart;
}): HTMLElement | HTMLElement[] {
  const today = LocalDate.now();
  const details = opts.enrollment;
  const backLink = (
    <div className={style.text_with_icon} style="margin-bottom: 12px;">
      <i className={FA.icon_arrow_left}></i>
      <a href={routes.student_startRoute.link()}>
        <div>{"Terug"}</div>
      </a>
    </div>
  );

  const header = renderEnrollmentCard(
    { showStudentName: true },
    details.enrollment,
  );

  const createdAbsencesRendered = mapPartial(
    opts.enrollment.student_absences,
    (abs) => {
      const lesson = opts.enrollment.lessons.find((l) => l.id === abs.lessonid);
      if (isNil(lesson)) {
        return null;
      }
      return { abs, lesson };
    },
  )
    .orderBy(({ lesson }) => lesson.date.toEpochDay())
    .map(({ abs, lesson }) => {
      return (
        <div className={style.panel} style="background-color: var(--grey-100)">
          <div style="margin-bottom: 8px;">
            <div style="font-weight: bold;">
              {"Afwezigheid doorgegeven voor les op %date".replace(
                "%date",
                ddMMMMyyyy(lesson.date),
              )}
            </div>
          </div>
          <div style="margin-bottom: 8px;" className={style.text_with_icon}>
            <i className={FA.icon_envelope_solid}></i>
            <div>
              {abs.comments ? (
                <span>{abs.comments}</span>
              ) : (
                <span style="text-style: italic;">
                  {"Geen reden doorgegeven"}
                </span>
              )}
            </div>
          </div>
          {abs.storagefileid ? (
            <div className={style.text_with_icon}>
              <i className={FA.icon_link_solid}></i>
              <a
                href={routes.downloadStudentAbsenceFileSpec.route.link({
                  lessonid: abs.lessonid,
                  studentid: details.enrollment.student.id,
                })}
                target="_blank"
              >
                {"Document opgeslagen"}
              </a>
            </div>
          ) : (
            fileboxWithAutosave({
              initiallabel: "Voeg document toe",
              selectedlabel: "",
              style: "",
              doSave: (file) =>
                rpc(routes.uploadStudentAbsenceFileSpec, undefined, {
                  lessonid: abs.lessonid,
                  studentid: details.enrollment.student.id,
                  file: {
                    mimetype: file.mimetype,
                    fileb64: arrayBufferToBase64Client(file.data),
                  },
                }).finally(() => document.location.reload()),
            })
          )}
        </div>
      );
    });

  const registeringAbsenceS = new Source(false);

  const register = dynVariadic([registeringAbsenceS], ([registering]) => {
    if (registering === false) {
      return (
        <section style="text-align: right; margin-top: 16px;">
          <button
            style="display: inline-block; width: 100%;"
            className={style.button_accent + style.text_with_icon}
            onclick={() => registeringAbsenceS.set(true)}
          >
            <i className={FA.icon_envelope_solid}></i>
            <div>{"Registreer je afwezigheid"}</div>
          </button>
        </section>
      );
    } else {
      const form = new Form({})
        .addField("lesson", [], () => {
          const lessons = opts.enrollment.lessons
            .filter((l) => l.removedreason === null)
            .filter((l) => l.date.isEqual(today) || l.date.isAfter(today))
            .orderBy((l) => l.date.toEpochDay());

          return selectBox({
            initial: undefined,
            values: lessons,
            label: "Op welke datum zal je afwezig zijn?",
            isDisabled: (l) =>
              opts.enrollment.student_absences.some(
                (abs) => abs.lessonid === l.id,
              ),
            show: (l) => {
              const existingAbsence = opts.enrollment.student_absences.some(
                (abs) => abs.lessonid === l.id,
              );
              return (
                ddMMMMyyyy(l.date) +
                (existingAbsence
                  ? " " + wrapInParentheses("Al geregistreerd")
                  : "")
              );
            },
          });
        })
        .addField("comments", [], () =>
          textBox({
            label: "Waarom zal je afwezig zijn?",
            type: "textarea",
            validations: [mandatoryValidation],
          }),
        )
        .addField("file", [], async () =>
          fileInputField({
            initial: null,
            label: "Document",
            initiallabel: "Voeg document toe",
            selectedlabel: "",
          }),
        )
        .build();

      return (
        <section className={style.panel}>
          {form.render()}
          <div style="text-align: right">
            <button onclick={() => registeringAbsenceS.set(false)}>
              {"Annuleer"}
            </button>
            {renderSaveButton({
              formS: form.s,
              text: {
                normal: "Verzend",
                success: "Gelukt!",
              },
              reload: true,
              timeout: 1500,
              save: async (form) => {
                return rpc(routes.saveStudentAbsenceSpec, undefined, {
                  lessonid: form.lesson.id,
                  studentid: details.enrollment.student.id,
                  comments: form.comments,
                  file:
                    form.file && form.file.tag === "new"
                      ? form.file.file
                      : null,
                });
              },
            })}
          </div>
        </section>
      );
    }
  });

  return [
    <div
      className={style.full_width_center_800}
      style="background-color: var(--grey-100);"
    >
      {backLink}
      {header}
    </div>,
    <main
      className={style.full_width_center_800}
      style="max-width: unset; background-color: white "
    >
      {register}
      {createdAbsencesRendered}
    </main>,
  ];
}
